import React from 'react';
import {
  CheckCircle2, IndianRupee, Users, ClipboardList, Clock, AlertCircle, ShieldAlert, HelpCircle, Sparkles, FileText,
} from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { useLanguage } from '../i18n/LanguageContext';
import { useServiceSEO } from '../hooks/useServiceSEO';

/**
 * Default 5-step flow shown when a service doesn't define its own `process`.
 * Shared across every service that uses it, so it gets ONE translation
 * (`svccontent._default.*`) instead of a duplicate per service — step 0's
 * description takes `{service}` as an interpolation placeholder.
 */
const DEFAULT_PROCESS_STEPS = [
  { titleFb: 'Share your details', descFb: 'Fill the form on this page with your name, mobile number and a few details about your {service} requirement.', interpolate: true },
  { titleFb: 'A dedicated expert calls you', descFb: 'Our expert calls within 2 hours to understand your case and confirm the documents needed.' },
  { titleFb: 'We prepare everything', descFb: 'Your expert prepares the application or filing and shares it with you for confirmation before submission.' },
  { titleFb: 'We file & track it', descFb: "It's filed with the relevant department on your behalf, and we track it through to completion." },
  { titleFb: 'Delivered to you', descFb: 'You receive the completed work along with all relevant documents and confirmation.' },
];

/**
 * Generic, data-driven "everything you need to know" section for a service page.
 * Renders whichever pieces the given content object provides — every field is optional
 * except `intro` and `faqs`, so short entries stay short instead of showing empty sections.
 */
const ServiceContent = ({ code, title, content }) => {
  const { t } = useLanguage();
  useServiceSEO({ code, title, content });
  if (!content) return null;
  const {
    intro = [], table, eligibilityTitle, eligibility, documents, benefitsTitle, benefits,
    process, timeline, mistakes, penalties, faqs = [],
  } = content;
  const usingDefaultProcess = !process;
  const kb = (key) => `svccontent.${code}.${key}`;
  const stepKey = (idx, field) => (usingDefaultProcess ? `svccontent._default.process_step${idx}_${field}` : kb(`process_step${idx}_${field}`));
  const steps = process || DEFAULT_PROCESS_STEPS.map((s) => ({ title: s.titleFb, desc: s.descFb, interpolate: s.interpolate }));

  return (
    <div className="mt-10 space-y-10">
      {intro.length > 0 && (
        <div className="max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0f2540] mb-4">
            {t(kb('title'), `${title} — everything you need to know`)}
          </h2>
          {intro.map((p, idx) => (
            <p key={idx} className="text-gray-600 leading-relaxed mb-3 last:mb-0">
              {t(kb(`intro${idx}`), p)}
            </p>
          ))}
        </div>
      )}

      {table && (
        <div>
          <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-[#14A098]" /> {t(kb('table_title'), table.title)}
          </h3>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm text-left">
              <thead className="bg-teal-50/60">
                <tr>
                  {table.columns.map((col, colIdx) => (
                    <th key={col} className="px-4 py-3 font-semibold text-[#0f2540]">{t(kb(`table_col${colIdx}`), col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {table.rows.map((row, rowIdx) => (
                  <tr key={row[0]}>
                    {row.map((cell, idx) => (
                      <td key={idx} className={`px-4 py-3 ${idx === 0 ? 'text-gray-700' : idx === 1 ? 'font-semibold text-[#0f2540] whitespace-nowrap' : 'text-gray-500'}`}>
                        {t(kb(`table_row${rowIdx}_cell${idx}`), cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {eligibility && eligibility.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#14A098]" /> {t(kb('eligibility_title'), eligibilityTitle || `Who needs ${title}?`)}
          </h3>
          <ul className="grid sm:grid-cols-2 gap-3">
            {eligibility.map((item, idx) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700 bg-white rounded-xl border border-gray-100 px-3.5 py-3">
                <CheckCircle2 className="w-4 h-4 text-[#14A098] mt-0.5 shrink-0" />
                <span>{t(kb(`eligibility${idx}`), item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {documents && documents.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#14A098]" /> {t(kb('documents_title'), 'Documents required')}
          </h3>
          <ul className="grid sm:grid-cols-2 gap-3">
            {documents.map((item, idx) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700 bg-white rounded-xl border border-gray-100 px-3.5 py-3">
                <CheckCircle2 className="w-4 h-4 text-[#14A098] mt-0.5 shrink-0" />
                <span>{t(kb(`document${idx}`), item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {benefits && benefits.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#14A098]" /> {t(kb('benefits_title'), benefitsTitle || `Benefits of ${title}`)}
          </h3>
          <ul className="grid sm:grid-cols-2 gap-3">
            {benefits.map((item, idx) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700 bg-white rounded-xl border border-gray-100 px-3.5 py-3">
                <CheckCircle2 className="w-4 h-4 text-[#14A098] mt-0.5 shrink-0" />
                <span>{t(kb(`benefit${idx}`), item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-[#14A098]" /> {t(kb('process_title'), `How SolveTax handles your ${title}`)}
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {steps.map((step, idx) => (
            <div key={step.title} className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
              <span className="w-8 h-8 shrink-0 rounded-full bg-[#14A098] text-white text-sm font-bold flex items-center justify-center">{idx + 1}</span>
              <div>
                <div className="font-semibold text-[#0f2540] text-sm mb-1">{t(stepKey(idx, 'title'), step.title)}</div>
                <div className="text-sm text-gray-500 leading-snug">
                  {t(stepKey(idx, 'desc'), step.desc, step.interpolate ? { service: title } : undefined)}
                </div>
              </div>
            </div>
          ))}
          {timeline && (
            <div className="flex items-start gap-3 rounded-2xl border border-[#14A098]/25 bg-teal-50/50 px-4 py-4">
              <Clock className="w-5 h-5 text-[#14A098] shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-[#0f2540] text-sm mb-1">{t(kb('timeline_title'), 'Typical timeline')}</div>
                <div className="text-sm text-gray-600 leading-snug">{t(kb('timeline'), timeline)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(mistakes?.length > 0 || penalties?.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-8">
          {mistakes && mistakes.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#14A098]" /> {t(kb('mistakes_title'), 'Common mistakes to avoid')}
              </h3>
              <div className="space-y-3">
                {mistakes.map((item, idx) => (
                  <div key={item.title} className="rounded-xl border border-gray-100 bg-white px-4 py-3">
                    <div className="font-semibold text-[#0f2540] text-sm mb-1">{t(kb(`mistake${idx}_title`), item.title)}</div>
                    <div className="text-sm text-gray-500 leading-snug">{t(kb(`mistake${idx}_desc`), item.desc)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {penalties && penalties.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-[#14A098]" /> {t(kb('penalties_title'), 'Penalties for non-compliance')}
              </h3>
              <div className="space-y-3">
                {penalties.map((item, idx) => (
                  <div key={item.title} className="rounded-xl border border-rose-100 bg-rose-50/40 px-4 py-3">
                    <div className="font-semibold text-[#0f2540] text-sm mb-1">{t(kb(`penalty${idx}_title`), item.title)}</div>
                    <div className="text-sm text-gray-600 leading-snug">{t(kb(`penalty${idx}_desc`), item.desc)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {faqs.length > 0 && (
        <div className="max-w-3xl">
          <h3 className="text-lg font-bold text-[#0f2540] mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#14A098]" /> {t(kb('faq_title'), 'Frequently asked questions')}
          </h3>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, idx) => (
              <AccordionItem key={item.q} value={`${code}-faq-${idx}`} className="border-gray-100">
                <AccordionTrigger className="text-left text-[#0f2540] font-semibold hover:no-underline hover:text-[#14A098] text-sm">
                  {t(kb(`faq_q${idx}`), item.q)}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed text-sm">
                  {t(kb(`faq_a${idx}`), item.a)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
};

export default ServiceContent;
